<template>
  <div
    ref="elementRef"
    v-style="brandStyles[isFullHeight ? 'containerFullHeight' : 'container']?.(headerHeight, promoBarHeight)"
    v-style:bg="backgroundColor"
    class="relative grid"
    :class="{ 'children:h-inherit': isFullHeight }"
  >
    <div class="relative md:grid-area-stack">
      <base-picture
        v-if="responsiveMedia.images"
        v-style:max-h="cinemaModeMaxH"
        v-bind="responsiveMedia.images"
        class="full"
        fit="cover"
        :lazy
        :props-img="{ class: 'h-full' }"
      />
      <vf-video
        v-else-if="responsiveMedia.video"
        v-bind="responsiveMedia.video"
        ref="videoRef"
        v-style:max-h="cinemaModeMaxH"
        :autoplay="!lazy"
        class="full"
        :controls
        fit="cover"
        loop
        muted
      />
      <cms-base-gradient
        v-if="gradientStyle && gradientStyle !== 'off'"
        v-bind="{ gradientTone, gradientStyle, gradientBreakpoints }"
        position="bottom"
      />
      <base-link
        v-if="mediaLink"
        aria-hidden="true"
        class="absolute-0"
        :data-cta="mediaLink.delocalizedTracking"
        tabindex="-1"
        :target="mediaLink.targetAttribute"
        :to="mediaLink.url"
        @click="handlePromotionClick('img')"
      />
      <cms-base-caption v-if="hasCaption" v-bind="{ captionColor, captionStyle, captionPosition, captionText }" />
    </div>
    <div
      class="z-1 md:pointer-events-none md:flex md:grid-area-stack <md:my-6"
      :class="[
        contentAlignmentSmall[alignmentSmall],
        contentAlignmentLarge[alignmentLarge],
        textsAlignmentSmall[textAlignmentSmall],
        textsAlignmentLarge[textAlignmentLarge],
        { 'items-center': !!contentAlignmentLargeOld[alignmentLarge] },
        brandClasses!.containerWrapper,
      ]"
    >
      <div
        v-style:bg="backdrop"
        v-style:pb="{ sm: firstElement && floatingNavigation && isFullHeight ? '5rem' : 0, lg: 0 }"
        :class="[
          overlayWidth ? contentSize[overlayWidth] : brandClasses!.containerWidth,
          brandClasses!.container,
        ]"
      >
        <cms-base-eyebrow
          class="mb-2"
          v-bind="{
            eyebrowLogoResponsive,
            eyebrowText,
            eyebrowTextColor,
          }"
        />
        <p v-if="overline" v-style:c="overlineColor" :class="overlineStyle" data-test-id="cms-banner-overline">
          {{ overline }}
        </p>
        <component
          :is="titleTag"
          v-if="title"
          v-style:c="titleColor"
          :class="[titleStyle, { 'sr-only': hideTitle }]"
          data-test-id="cms-banner-title"
        >
          <base-link
            v-if="mediaLink"
            :data-cta="mediaLink.delocalizedTracking"
            :target="mediaLink.targetAttribute"
            :to="mediaLink.url"
            @click="handlePromotionClick('title')"
          >
            <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
            <span v-else>{{ title }}</span>
          </base-link>
          <template v-else>
            <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
            <span v-else>{{ title }}</span>
          </template>
        </component>
        <p
          v-if="subtitle"
          v-style:c="subtitleColor"
          :class="subtitleStyle"
          data-test-id="cms-banner-text"
        >
          <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
          <span v-else>{{ subtitle }}</span>
        </p>
        <cms-rich-text v-if="richText" v-style:c="richTextColor || subtitleColor" class="pointer-within" :content="{ richText }" />
        <div
          v-if="linkType !== 'No-CTA' && targets.length"
          class="pointer-within f-col gap-4 md:flex-row md:items-center md:wrap lg:gap-6 not-first:pt-4 !pt-0"
          :class="[
            ctaAlignmentSmall[alignmentSmall],
            ctaAlignmentSmall[textAlignmentSmall],
            ctaAlignmentLarge[alignmentLarge],
            ctaAlignmentLarge[textAlignmentLarge],
            equalTargetSizeClass,
          ]"
          :style="{ '--cols': cols }"
        >
          <cms-shared-button
            v-for="(target, i) in targets"
            :key="i"
            v-bind="{ ...target, ...getTextLinkStyles(content) }"
            :size
            @click="handlePromotionClick(`cta${i + 1}`)"
          />
        </div>
        <cms-rich-text
          v-if="richTextBelowTargets"
          v-style:c="richTextBelowTargetsColor || subtitleColor"
          class="pointer-within"
          :content="{ richText: richTextBelowTargets }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { Responsive } from '#types/common'
import type {
  BannerImageAlignmentLarge,
  BannerImageAlignmentSmall,
  BannerImageContent
} from '#types/components/cms/banner-image'
import type { TextAlignment } from '#types/components/cms/text-alignment'
import type { Target } from '#types/target'
import { SectionContextKey } from '#content/contexts/section'

const { controls = true, ...props } = defineProps<{
  content: BannerImageContent
  controls?: boolean
  paused?: boolean
  firstElement?: boolean
}>()

const {
  subtitleColor,
  alignmentLarge,
  alignmentSmall,
  backdrop,
  backgroundColor,
  captionPosition,
  captionStyle,
  captionText,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowText,
  eyebrowTextColor = subtitleColor,
  gradientBreakpoints,
  gradientStyle,
  gradientTone,
  hideTitle,
  isFullHeight,
  linkType,
  media,
  mediaTarget,
  overlayWidth,
  cinemaMode,
  overline,
  overlineColor,
  overlineStyle,
  promotionTracking,
  richText,
  richTextBelowTargets,
  richTextBelowTargetsColor,
  richTextColor,
  subtitle,
  subtitleHtml,
  subtitleStyle,
  captionColor = subtitleColor,
  targets = [] as Target[],
  textAlignmentLarge,
  textAlignmentSmall,
  title,
  titleHtml,
  titleColor,
  titleH1,
  titleStyle,
} = props.content

const { size } = useAppConfig().components.cms.sharedButton
const { brandClasses, brandStyles } = useAppConfig().components.cms.imageBanner
const { floatingNavigation, promoBarHeight } = useAppConfig().components.vf.header
const { getMedia } = useCms()
const header = useHeaderStore()

const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, props.content.name)

const { lazy } = inject(SectionContextKey, {} as SectionProvider)
const videoRef = ref()
const mediaLink = mediaTarget || targets[0]
const responsiveMedia = getMedia(media)

const headerHeight = computed(() => (header.transparent ? header.height.promoBar : header.height.full) || 0)

const cinemaModeMaxH = computed(() =>
  Object.entries(cinemaMode || {}).reduce((acc, [key, value]) => {
    acc[key as keyof Responsive] = value ? '50vh' : 'auto'
    return acc
  }, {} as Responsive<string>)
)

/**
 * @deprecated Backwards compatibility for old alignment values
 */
const contentAlignmentLargeOld: Partial<Record<BannerImageAlignmentLarge, string>> = {
  'left-center': 'md:justify-center',
  'left-right': 'md:justify-end',
  'center-left': 'md:text-center',
  'center-center': 'md:text-center md:justify-center',
  'center-right': 'md:text-center md:justify-end',
}

const contentAlignmentLarge: Partial<Record<BannerImageAlignmentLarge, string>> = {
  ...contentAlignmentLargeOld,
  'bottom-center': 'md:items-end md:justify-center',
  'bottom-left': 'md:items-end',
  'bottom-right': 'md:items-end md:justify-end',
  'middle-center': 'md:items-center md:justify-center',
  'middle-left': 'md:items-center',
  'middle-right': 'md:items-center md:justify-end',
  'top-center': 'md:items-start md:justify-center',
  'top-left': 'md:items-start',
  'top-right': 'md:items-start md:justify-end',
}

const contentAlignmentSmall: Partial<Record<BannerImageAlignmentSmall, string>> = {
  center: '<md:items-center <md:text-center',
  overlay: '<md:items-end <md:absolute <md:bottom-0 <md:h-a <md:w-full',
}

const textsAlignmentSmall: Record<TextAlignment, string> = {
  left: 'text-start',
  center: 'text-center',
  right: 'text-end'
}

const textsAlignmentLarge: Record<TextAlignment, string> = {
  left: 'md:text-start',
  center: 'md:text-center',
  right: 'md:text-end'
}

const ctaAlignmentLarge: Partial<Record<BannerImageAlignmentLarge | TextAlignment, string>> = {
  'center-left': 'md:text-center md:justify-center',
  'center-center': 'md:text-center md:justify-center',
  'center-right': 'md:text-center md:justify-center',
  'left': 'md:text-start md:justify-start',
  'center': 'md:text-center md:justify-center',
  'right': 'md:text-end md:justify-end'
}

const ctaAlignmentSmall: Record<BannerImageAlignmentSmall | TextAlignment, string> = {
  left: '<md:items-start',
  center: '<md:items-center <md:text-center',
  right: '<md:items-end',
  overlay: ''
}

const equalTargetSizeClass = [
  getValueForBreakpoint('sm', equalTargetSize) && '<md:grid <md:cols-1 <md:children:full',
  getValueForBreakpoint('md', equalTargetSize) && 'md:grid md:cols-$cols md:auto-rows-fr md:children:full',
].filter(Boolean).join(' ')

const hasCaption = captionText && captionPosition !== 'off'

const contentSize = {
  xs: 'md:w-1/3',
  sm: 'md:w-1/2',
  md: 'md:w-2/3',
  lg: 'md:w-11/12'
}

const cols = targets.map((target) => target.style?.sm === 'text-link' ? 'max-content' : 'minmax(0,1fr)').join(' ')

const titleTag = titleH1 ? 'h1' : 'h2'

onMounted(() => videoRef.value?.play().catch((e) => log.error(e)))

watch(() => props.paused, (paused) => paused ? videoRef.value?.pause() : videoRef.value?.play())
</script>
